import React from 'react';

import { Box, Skeleton, Stack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { REVIEW_RESULT_VIEW, useSelectedReviewResultView, useCurrentProvider } from 'reducers/reviewResultSlice';
import useReviewResultParams from 'screens/performanceReview/hooks/useReviewResultParams';

import { ExpandableContainerProvider, useExpandableContainer } from 'uiToolkitV1/ExpandableContainerProvider';
import { Paper, PaperWithShadow } from 'uiToolkitV1/Paper';
import { SpiderChart } from 'uiToolkitV1/SpiderChart/SpiderChart';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useRatingResultData } from './hooks/dataHooks';

const ChartContainer = styled(Box)`
  & p {
    font-size: 14px !important;
  }
`;

const SpiderChartComp = ({ competencyGraphData, providerTypes, title, isBigChart = false }) => {
  const { isExpanded = false } = useExpandableContainer();

  return (
    <ChartContainer height="100%">
      <TextBodyMedium bold ml={6} mt={6}>
        {title}
      </TextBodyMedium>
      <SpiderChart
        // eslint-disable-next-line no-nested-ternary
        height={isBigChart ? 500 : isExpanded ? 600 : 400}
        data={competencyGraphData}
        labelDetails={providerTypes}
        isBigChart={isBigChart}
      />
    </ChartContainer>
  );
};

const ResultSpiderChart = ({
  title,
  // isBigChart is specifically for the chart rendered to be screenshot by puppeteer, for the PDF
  isBigChart = false,
}) => {
  const { reviewId, employeeId, providerId } = useReviewResultParams();
  const selectedView = useSelectedReviewResultView();
  const currentProvider = useCurrentProvider();

  const { data, isLoading } = useRatingResultData({
    reviewId,
    employeeId,
    providerId,
    isIndividualReviewResult: selectedView === REVIEW_RESULT_VIEW.PEOPLE,
    responseId: currentProvider?.reviewId,
    providerType: currentProvider?.providerType,
  });
  const { competencyGraphData, providerTypes } = data ?? {};

  if (isLoading) {
    return (
      <Paper
        bg="#F5F7F9"
        p="16px"
        sx={{
          boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
        }}
        mb="16px"
      >
        <Stack>
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </Stack>
      </Paper>
    );
  }

  if (!providerTypes || providerTypes?.length === 0) return null;
  return (
    <PaperWithShadow w="100%" py={0} h="100%" pb={4} mb={4}>
      <ExpandableContainerProvider isParentDeterminesHeight>
        <SpiderChartComp
          competencyGraphData={competencyGraphData}
          providerTypes={providerTypes}
          title={title}
          isBigChart={isBigChart}
        />
      </ExpandableContainerProvider>
    </PaperWithShadow>
  );
};

export default ResultSpiderChart;
